import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { apiGet } from '../lib/api.js';
import { Callout } from '../ui/Callout.js';
import { DataRow, DataTable, TableCell, TableHead } from '../ui/DataTable.js';
import { StatusBadge, type Status as BadgeStatus } from '../components/StatusBadge.js';
import { formatCount, formatRelativeTime } from '../lib/format.js';

type LivenessDomain = {
  domainId: number;
  hostname: string;
  groupId: number;
  groupName: string;
  enabled: boolean;
  entryCount: number;
  lastSeenAt: string | null;
  lastMissAt: string | null;
};

type Status = {
  /** This screen's own vocabulary, richer than the three generic states. */
  label: string;
  /**
   * Which badge treatment carries the label. `deleted` is the neutral,
   * resting treatment — a domain with no paths is unconfigured, not broken.
   */
  badge: BadgeStatus;
  /** Lower sorts first: the domains that need attention lead the table. */
  rank: number;
};

/**
 * "Configured but never seen" is the failure this screen exists for.
 *
 * A hostname with no traffic at all is almost never a weighting mistake — it is
 * DNS pointing elsewhere, an ingress host rule that omits the name, or a
 * certificate that does not cover it. None of that is visible from the
 * configuration screens, so it is named here in words rather than left as an
 * empty cell.
 */
function statusOf(domain: LivenessDomain): Status {
  if (domain.entryCount === 0) return { label: 'No paths configured', badge: 'deleted', rank: 1 };
  if (domain.lastSeenAt !== null) return { label: 'Live', badge: 'active', rank: 3 };
  if (domain.lastMissAt !== null)
    return { label: 'Arriving, nothing matched', badge: 'paused', rank: 2 };
  return { label: 'Never seen', badge: 'paused', rank: 0 };
}

export function LivenessScreen() {
  const navigate = useNavigate();
  const liveness = useQuery({
    queryKey: ['liveness'],
    queryFn: () => apiGet<{ domains: LivenessDomain[] }>('/api/reports/liveness'),
    refetchInterval: 30_000,
  });

  const domains = liveness.data?.domains ?? [];

  const unseen = domains.filter(
    (domain) => domain.lastSeenAt === null && domain.lastMissAt === null && domain.entryCount > 0,
  );

  const ordered = [...domains].sort((a, b) => {
    const byStatus = statusOf(a).rank - statusOf(b).rank;
    return byStatus === 0 ? a.hostname.localeCompare(b.hostname) : byStatus;
  });

  return (
    <section aria-labelledby="liveness-heading" className="flex flex-col gap-(--space-5)">
      <header>
        <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
          Delivery
        </p>
        <h1 id="liveness-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
          Domains
        </h1>
        <p className="mt-(--space-2) max-w-[64ch] text-ink-muted">
          Whether traffic is actually arriving on each configured hostname. A domain that has never
          been seen is usually a DNS or ingress problem rather than a configuration one.
        </p>
      </header>

      {unseen.length > 0 && (
        <Callout tone="warn">
          <p>
            {unseen.length} configured domain{unseen.length === 1 ? '' : 's'} ha
            {unseen.length === 1 ? 's' : 've'} never received a single request. Check DNS resolves to
            this service, that the ingress host rule includes the hostname, and that the TLS
            certificate covers it.
          </p>
          <ul className="mt-(--space-2) flex flex-col gap-(--space-1) font-mono">
            {unseen.map((domain) => (
              <li key={domain.domainId}>{domain.hostname}</li>
            ))}
          </ul>
        </Callout>
      )}

      {liveness.isError && (
        <Callout tone="danger">Could not load domain liveness. Retrying every 30 seconds.</Callout>
      )}

      {liveness.data !== undefined && (
        <DataTable
          caption="Configured domains and when they last received traffic"
          head={
            <>
              <TableHead scope="col">Hostname</TableHead>
              <TableHead scope="col">Group</TableHead>
              <TableHead scope="col" className="text-right">
                Paths
              </TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">Last redirect</TableHead>
              <TableHead scope="col">Last miss</TableHead>
            </>
          }
        >
          {ordered.map((domain) => {
            const status = statusOf(domain);
            return (
              <DataRow
                key={domain.domainId}
                onActivate={() => void navigate(`/domains/${domain.domainId}`)}
                activateLabel={`Open domain ${domain.hostname}`}
              >
                <TableCell className="py-(--space-3) font-mono text-[length:var(--text-small)] text-ink">
                  {domain.hostname}
                  {!domain.enabled && (
                    <span className="ml-(--space-2) font-sans text-warn">disabled</span>
                  )}
                </TableCell>
                <TableCell className="py-(--space-3) text-[length:var(--text-small)]">
                  {/*
                   * The only link left in the row. It stops propagation so a
                   * click meant for the group does not also open the domain.
                   */}
                  <Link
                    to={`/groups/${domain.groupId}`}
                    className="underline-offset-4 hover:underline"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {domain.groupName}
                  </Link>
                </TableCell>
                <TableCell className="py-(--space-3) text-right tabular-nums">
                  {formatCount(domain.entryCount)}
                </TableCell>
                <TableCell className="py-(--space-3)">
                  <StatusBadge status={status.badge} label={status.label} />
                </TableCell>
                <TableCell className="py-(--space-3) text-[length:var(--text-small)]">
                  <span className={domain.lastSeenAt === null ? 'text-warn' : 'text-ink'}>
                    {formatRelativeTime(domain.lastSeenAt)}
                  </span>
                </TableCell>
                <TableCell className="py-(--space-3) text-[length:var(--text-small)] text-ink-muted">
                  {domain.lastMissAt === null ? '—' : formatRelativeTime(domain.lastMissAt)}
                </TableCell>
              </DataRow>
            );
          })}
        </DataTable>
      )}
    </section>
  );
}
